import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid-pro';
import { toJS } from 'mobx';
import { observer } from 'mobx-react-lite';
import { IMaterialsSFilter } from 'src/@types/SearchFilterTypes';
import CDataGridPro from 'src/components/CDataGridPro';
import { CDownloadDialog } from 'src/components/custom-download/CDownloadDIalog';
import { CGridFooter } from 'src/components/custom-grid-data-components';
import { useDialogContext } from 'src/components/dialog/DialogContext';
import useCallApiWithLoading from 'src/hooks/useCallApiWithLoading';
import useGetGridCol, { IMaterialsGridRow } from 'src/hooks/useGetGridCol';
import { ContentBox, ContentBoxButtons, ContentButtonCreate } from 'src/layouts/form/FormButton';
import { useStores } from 'src/models';
import { INIT_PAGINATION, IPagination } from 'src/models/extensions/withPagination';
import { materialsApi } from 'src/service';
import { FORM_IDS, FormType } from 'src/static/constants/common/form';

import { MaterialsFormDialog } from './MaterialsFormDialog';

export interface IMaterialalDownladProps {
  materialSid: number;
  filePath: string[];
  isZipFile: boolean;
}
interface IMaterialGridProps {
  searchData: IMaterialsSFilter;
}

export const MaterialGrid = ({ searchData }: IMaterialGridProps) => {
  const { getMaterialsGridCol } = useGetGridCol();
  const { callApiWithLoading } = useCallApiWithLoading();
  const { openDialog, closeDialog } = useDialogContext();
  const { alertStore } = useStores();
  const { t } = useTranslation();
  // 그리드 row 데이터
  const [gridRows, setGridRows] = useState<IMaterialsGridRow[]>([]);
  // 페이지네이션
  const [pagination, setPagination] = useState<IPagination>(INIT_PAGINATION);

  // 목록 조회
  const getData = useCallback(async () => {
    const formData = {
      ...searchData,
      page: pagination.page,
      size: pagination.size,
    };
    await materialsApi.getMaterials(formData).then((res) => {
      if (res) {
        const newGridRows = res.data.map((material, i) => {
          return { ...material, id: material.materialSid ?? i };
        });
        setGridRows(newGridRows);
        if (res.pagination) {
          setPagination(res.pagination);
        }
      }
    });
  }, [searchData, pagination.page, pagination.size]);

  useEffect(() => {
    getData();
  }, [getData]);

  // 등록 버튼: 등록 dialog open
  const handleAdd = () => {
    openDialog({
      id: FORM_IDS.MATERIALS,
      formType: FormType.CREATE,
      maxWidth: 'md',
      dialogContent: (
        <MaterialsFormDialog formType={FormType.CREATE} onSubmitSuccess={handleSubmitSuccess} />
      ),
    });
  };

  // 그리드 더블 클릭 시: 수정 dialog open
  const handleRowDoubleClick = (param: GridRowParams<IMaterialsGridRow>) => {
    callApiWithLoading(async () => {
      await materialsApi.getDetail(param.row.materialSid).then((res) => {
        if (res) {
          openDialog({
            id: FORM_IDS.MATERIALS,
            formType: FormType.UPDATE,
            maxWidth: 'md',
            dialogContent: (
              <MaterialsFormDialog
                formType={FormType.UPDATE}
                onSubmitSuccess={handleSubmitSuccess}
                selectedData={res.data}
              />
            ),
          });
        }
      });
    });
  };

  // 등록 & 수정 성공 시
  const handleSubmitSuccess = () => {
    closeDialog(FORM_IDS.MATERIALS);
    getData();
  };

  // 페이지 모델 변경 시
  const handlePaginationModelChange = (paginationModel: GridPaginationModel) => {
    const newPagination = {
      ...pagination,
      page: paginationModel.page + 1,
      size: paginationModel.pageSize,
    };
    setPagination(newPagination);
  };

  // 자료 다운로드
  const handleDownloadClick = async (materialSid: number, title: string, filePath: string[]) => {
    if (filePath.length === 0) {
      alertStore.setProps({
        open: true,
        title: '다운로드',
        content: '파일이 존재하지 않습니다.',
        category: 'error',
      });
      return;
    }
    const attachment = {
      url: `/v1/material/${materialSid}`,
      sid: materialSid,
      filePath: filePath,
      isZipFile: false,
    };
    openDialog({
      id: FORM_IDS.RECRUIT_DOWNLOAD,
      title: '자료 다운로드',
      maxWidth: 'sm',
      dialogContent: <CDownloadDialog data={attachment} />,
    });
  };

  return (
    <ContentBox>
      <ContentBoxButtons>
        <ContentButtonCreate onClick={handleAdd}>{t('common.button.register')}</ContentButtonCreate>
      </ContentBoxButtons>
      <CDataGridPro
        columns={getMaterialsGridCol(handleDownloadClick)}
        rows={gridRows}
        rowCount={toJS(pagination.totalElements)}
        paginationModel={{
          page: pagination.page - 1,
          pageSize: pagination.size,
        }}
        onPaginationModelChange={handlePaginationModelChange}
        onRowDoubleClick={handleRowDoubleClick}
        slots={{ footer: CGridFooter }}
      />
    </ContentBox>
  );
};

export default observer(MaterialGrid);
